import { supabase } from '../lib/supabase';
import { reportError } from '../lib/errorHandling';
import {
  Badge,
  UserBadge,
  StreakInfo,
  GamificationProfile,
  GamificationNotification,
} from '../types';

// Level formula: floor(sqrt(points / 50)) + 1
function calculateLevel(totalPoints: number): number {
  return Math.max(1, Math.floor(Math.sqrt(totalPoints / 50)) + 1);
}

function pointsForLevel(level: number): number {
  return (level - 1) * (level - 1) * 50;
}

export const gamificationService = {
  /**
   * Record daily activity for streak tracking.
   * Returns streak info + optional milestone number.
   */
  async recordActivity(userId: string): Promise<{ streak: StreakInfo; streakMilestone: number | null }> {
    const { data, error } = await supabase.rpc('record_daily_activity', {
      p_user_id: userId,
    });

    if (error) {
      // Best-effort: a failed streak update must not block app init.
      reportError('gamificationService.recordActivity', error, { userId });
      return {
        streak: { currentStreak: 0, longestStreak: 0, totalActiveDays: 0, lastActivityDate: null },
        streakMilestone: null,
      };
    }

    return {
      streak: {
        currentStreak: data.current_streak,
        longestStreak: data.longest_streak,
        totalActiveDays: data.total_active_days,
        lastActivityDate: new Date().toISOString().split('T')[0],
      },
      streakMilestone: data.streak_milestone ?? null,
    };
  },

  /**
   * Get current streak info for a user
   */
  async getStreak(userId: string): Promise<StreakInfo> {
    const { data, error } = await supabase
      .from('user_streaks')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error || !data) {
      // No row is normal for new users; an actual query error is not.
      if (error) reportError('gamificationService.getStreak', error, { userId });
      return { currentStreak: 0, longestStreak: 0, totalActiveDays: 0, lastActivityDate: null };
    }

    return {
      currentStreak: data.current_streak,
      longestStreak: data.longest_streak,
      totalActiveDays: data.total_active_days,
      lastActivityDate: data.last_activity_date,
    };
  },

  /**
   * Get all badge definitions for the achievement ladder.
   * Excludes 'course' badges — those are course-specific rewards that only
   * surface in a learner's collection once earned (via getMyBadges), so they
   * never appear as locked entries in the shelf.
   */
  async getAllBadges(): Promise<Badge[]> {
    const { data, error } = await supabase
      .from('badges')
      .select('*')
      .neq('category', 'course')
      .order('sort_order');

    if (error) throw new Error(`Failed to fetch badges: ${error.message}`);

    return (data || []).map((b: any) => ({
      id: b.id,
      name: b.name,
      description: b.description,
      icon: b.icon,
      category: b.category,
      tier: b.tier,
      sortOrder: b.sort_order,
    }));
  },

  /**
   * Get badges earned by the current user
   */
  async getMyBadges(userId: string): Promise<UserBadge[]> {
    const { data, error } = await supabase
      .from('user_badges')
      .select('id, badge_id, earned_at, badges(*)')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch user badges: ${error.message}`);

    return (data || []).map((ub: any) => ({
      id: ub.id,
      badgeId: ub.badge_id,
      earnedAt: new Date(ub.earned_at),
      badge: {
        id: ub.badges.id,
        name: ub.badges.name,
        description: ub.badges.description,
        icon: ub.badges.icon,
        category: ub.badges.category,
        tier: ub.badges.tier,
        sortOrder: ub.badges.sort_order,
      },
    }));
  },

  /**
   * Award a badge to a user (idempotent - ON CONFLICT DO NOTHING)
   */
  async awardBadge(userId: string, badgeId: string): Promise<boolean> {
    // Fetch user's tenant_id for RLS compliance
    const { data: profile } = await supabase
      .from('users')
      .select('tenant_id')
      .eq('id', userId)
      .single();

    if (!profile?.tenant_id) {
      reportError('gamificationService.awardBadge', new Error('Could not resolve tenant_id for user'), { userId, badgeId });
      return false;
    }

    const { error } = await supabase
      .from('user_badges')
      .upsert(
        { user_id: userId, badge_id: badgeId, tenant_id: profile.tenant_id },
        { onConflict: 'user_id,badge_id', ignoreDuplicates: true }
      );

    if (error) {
      reportError('gamificationService.awardBadge', error, { userId, badgeId });
      return false;
    }
    return true;
  },

  /**
   * Create a tenant-owned custom badge (category 'course') for use as a course's
   * completion reward. Returns the new badge id (a generated UUID string, since
   * badges.id is TEXT). RLS allows instructors/admins to insert within their tenant.
   */
  async createCourseBadge(
    tenantId: string,
    badge: { name: string; description: string; icon: string; tier: Badge['tier'] }
  ): Promise<string> {
    const id = crypto.randomUUID();
    const { error } = await supabase.from('badges').insert({
      id,
      tenant_id: tenantId,
      name: badge.name,
      description: badge.description,
      icon: badge.icon,
      category: 'course',
      tier: badge.tier,
      sort_order: 0,
    });

    if (error) throw new Error(`Failed to create course badge: ${error.message}`);
    return id;
  },

  /**
   * Update a tenant-owned custom badge in place (so re-saving a course's badge edits
   * the existing row rather than spawning duplicates). RLS scopes to the caller's tenant.
   */
  async updateCourseBadge(
    badgeId: string,
    badge: { name: string; description: string; icon: string; tier: Badge['tier'] }
  ): Promise<void> {
    const { error } = await supabase
      .from('badges')
      .update({
        name: badge.name,
        description: badge.description,
        icon: badge.icon,
        tier: badge.tier,
      })
      .eq('id', badgeId);

    if (error) throw new Error(`Failed to update course badge: ${error.message}`);
  },

  /**
   * List this tenant's custom course badges (category 'course'), for reuse when a
   * designer wants to award an already-created badge on another course. RLS scopes
   * results to the caller's tenant.
   */
  async listCourseBadges(tenantId: string): Promise<Badge[]> {
    const { data, error } = await supabase
      .from('badges')
      .select('*')
      .eq('category', 'course')
      .eq('tenant_id', tenantId)
      .order('name');

    if (error) throw new Error(`Failed to fetch course badges: ${error.message}`);

    return (data || []).map((b: any) => ({
      id: b.id,
      name: b.name,
      description: b.description,
      icon: b.icon,
      category: b.category,
      tier: b.tier,
      sortOrder: b.sort_order,
    }));
  },

  /**
   * Award a course's completion badge to a user, if the course has one set.
   * Returns a notification for the toast queue, or null if there's nothing to award
   * (no badge linked, or already earned).
   */
  async awardCourseBadge(userId: string, courseId: string): Promise<GamificationNotification | null> {
    const { data: course } = await supabase
      .from('courses')
      .select('completion_badge_id')
      .eq('id', courseId)
      .maybeSingle();

    const badgeId = course?.completion_badge_id;
    if (!badgeId) return null;

    // Skip if already earned (keeps the toast from re-firing on re-completion).
    const { data: existing } = await supabase
      .from('user_badges')
      .select('id')
      .eq('user_id', userId)
      .eq('badge_id', badgeId)
      .maybeSingle();
    if (existing) return null;

    const awarded = await this.awardBadge(userId, badgeId);
    if (!awarded) return null;

    const { data: badgeRow } = await supabase
      .from('badges')
      .select('*')
      .eq('id', badgeId)
      .maybeSingle();
    if (!badgeRow) return null;

    return {
      id: crypto.randomUUID(),
      type: 'badge_earned',
      title: 'Badge Earned!',
      message: `You earned "${badgeRow.name}"`,
      badge: {
        id: badgeRow.id,
        name: badgeRow.name,
        description: badgeRow.description,
        icon: badgeRow.icon,
        category: badgeRow.category,
        tier: badgeRow.tier,
        sortOrder: badgeRow.sort_order,
      },
    };
  },

  /**
   * Check conditions and award qualifying badges.
   * Returns list of newly earned badges.
   */
  async checkAndAwardBadges(
    userId: string,
    context: {
      completedLessonCount?: number;
      completedCourseCount?: number;
      totalPoints?: number;
      level?: number;
      currentStreak?: number;
      perfectQuizCount?: number;
    }
  ): Promise<GamificationNotification[]> {
    const notifications: GamificationNotification[] = [];

    // Get currently earned badge IDs
    const earned = await this.getMyBadges(userId);
    const earnedIds = new Set(earned.map((b) => b.badgeId));

    // Badge conditions map
    const conditions: Array<{ id: string; check: () => boolean }> = [
      // Achievement
      { id: 'first-steps', check: () => (context.completedLessonCount ?? 0) >= 1 },
      { id: 'graduate', check: () => (context.completedCourseCount ?? 0) >= 1 },
      { id: 'scholar', check: () => (context.completedCourseCount ?? 0) >= 5 },
      { id: 'overachiever', check: () => (context.completedCourseCount ?? 0) >= 10 },
      // Streak
      { id: 'getting-warmed-up', check: () => (context.currentStreak ?? 0) >= 3 },
      { id: 'on-fire', check: () => (context.currentStreak ?? 0) >= 7 },
      { id: 'unstoppable', check: () => (context.currentStreak ?? 0) >= 14 },
      { id: 'legendary', check: () => (context.currentStreak ?? 0) >= 30 },
      // Mastery
      { id: 'perfectionist', check: () => (context.perfectQuizCount ?? 0) >= 1 },
      { id: 'quiz-master', check: () => (context.perfectQuizCount ?? 0) >= 10 },
      // Points
      { id: 'point-collector', check: () => (context.totalPoints ?? 0) >= 500 },
      { id: 'point-hunter', check: () => (context.totalPoints ?? 0) >= 2000 },
      { id: 'point-legend', check: () => (context.totalPoints ?? 0) >= 5000 },
      // Level
      { id: 'rising-star', check: () => (context.level ?? 1) >= 5 },
      { id: 'veteran', check: () => (context.level ?? 1) >= 10 },
    ];

    // Get all badge definitions for notification details
    const allBadges = (await this.getAllBadges()) as Badge[];
    const badgeMap = new Map<string, Badge>(
      allBadges.map((b) => [b.id, b] as [string, Badge]),
    );

    for (const { id, check } of conditions) {
      if (!earnedIds.has(id) && check()) {
        const awarded = await this.awardBadge(userId, id);
        if (awarded) {
          const badge = badgeMap.get(id);
          if (badge) {
            notifications.push({
              id: crypto.randomUUID(),
              type: 'badge_earned',
              title: 'Badge Earned!',
              message: `You earned "${badge.name}"`,
              badge,
            });
          }
        }
      }
    }

    return notifications;
  },

  /**
   * Update user level based on total points
   */
  async updateUserLevel(userId: string, totalPoints: number): Promise<{ level: number; leveledUp: boolean; previousLevel: number }> {
    // Get current level
    const { data: userData } = await supabase
      .from('users')
      .select('level')
      .eq('id', userId)
      .single();

    const previousLevel = userData?.level ?? 1;
    const newLevel = calculateLevel(totalPoints);

    if (newLevel !== previousLevel) {
      const { error } = await supabase
        .from('users')
        .update({ level: newLevel })
        .eq('id', userId);
      if (error) {
        // Don't claim a level-up that didn't persist; next completion retries.
        reportError('gamificationService.updateUserLevel', error, { userId, newLevel });
        return { level: previousLevel, leveledUp: false, previousLevel };
      }
    }

    return { level: newLevel, leveledUp: newLevel > previousLevel, previousLevel };
  },

  /**
   * Get the full gamification profile for a user
   */
  async getGamificationProfile(userId: string, totalPoints: number): Promise<GamificationProfile> {
    const [streak, badges] = await Promise.all([
      this.getStreak(userId),
      this.getMyBadges(userId),
    ]);

    const level = calculateLevel(totalPoints);
    const currentLevelPoints = pointsForLevel(level);
    const nextLevelPoints = pointsForLevel(level + 1);
    const pointsToNextLevel = nextLevelPoints - totalPoints;

    return {
      streak,
      badges,
      level,
      totalPoints,
      pointsToNextLevel: Math.max(0, pointsToNextLevel),
      nextLevelPoints,
    };
  },
};
